import { createRoot } from "react-dom/client";
import { useState, useEffect, useCallback, useRef } from "react";
import { ClerkProvider, useClerk, useSignIn, useSignUp } from "@clerk/clerk-react";
// RUN AN — signup_started. Guarded no-op wherever the page carries no Plausible tag, which
// includes every SPA surface that mounts AuthModal (AppShell, ClarkShell). See src/analytics.js.
import { track } from "../analytics.js";

const PUBLISHABLE_KEY =
  window.__CLERK_PUBLISHABLE_KEY ||
  document.querySelector("script[data-clerk-publishable-key]")?.getAttribute("data-clerk-publishable-key");

function waitForClerk(timeoutMs) {
  return new Promise((resolve) => {
    if (window.Clerk) return resolve(window.Clerk);
    let waited = 0;
    const step = 50;
    const t = setInterval(() => {
      if (window.Clerk) { clearInterval(t); resolve(window.Clerk); }
      else if ((waited += step) >= timeoutMs) { clearInterval(t); resolve(null); }
    }, step);
  });
}

// ── Inject modal CSS once (app page has no .cc-checkout-* styles) ──
const STYLE_ID = "cc-auth-modal-styles";
function injectStyles() {
  if (document.getElementById(STYLE_ID)) return;
  const el = document.createElement("style");
  el.id = STYLE_ID;
  el.textContent = `
  .cc-auth-root { position: fixed; inset: 0; z-index: 10001; pointer-events: none; visibility: hidden; }
  .cc-auth-root.is-open { pointer-events: auto; visibility: visible; }
  .cc-auth-scrim { position: absolute; inset: 0; background: var(--cc-scrim, rgba(0,28,70,0.60)); backdrop-filter: blur(6px); -webkit-backdrop-filter: blur(6px); opacity: 0; transition: opacity 0.22s ease; }
  .cc-auth-root.is-open .cc-auth-scrim { opacity: 1; }
  .cc-auth-panel { position: fixed; top: 50%; left: 50%; width: min(440px, 94vw); max-height: 92vh; background: var(--cc-card-alt, #f7f9fc); box-shadow: 0 32px 100px rgba(0,28,70,0.45), 0 6px 20px rgba(0,28,70,0.20); border-radius: 16px; padding: 0 0 32px; display: flex; flex-direction: column; transform: translate(-50%,-48%) scale(0.97); opacity: 0; transition: opacity 0.20s ease, transform 0.20s ease; overflow-y: auto; }
  .cc-auth-root.is-open .cc-auth-panel { transform: translate(-50%,-50%) scale(1); opacity: 1; }
  .cc-auth-header { background: var(--cc-chrome, #002368); padding: 26px 24px 26px; border-radius: 16px 16px 0 0; display: flex; flex-direction: column; align-items: center; text-align: center; gap: 14px; flex-shrink: 0; position: relative; }
  /* RUN GE - the dismiss control's edge was a literal rgba(255,255,255,0.22) and measured
     1.93-2.03:1 on the chrome band in all four gate themes; on the 15 lighter chromes a white
     alpha is worse still, which is the population RUN GP was caught measuring past.
     --cc-on-chrome-faint is the one on-chrome ink generate.mjs derives to hold 4.5:1 in all 19
     (worst 4.61 on m4-coral), so it clears the 3:1 non-text bar with room in every theme. The
     designed on-chrome EDGE and FILL tokens deliberately do not: they are decorative washes,
     and forcing a control boundary onto one would be making a number green against intent. */
  .cc-auth-close { position: absolute; top: 14px; right: 14px; width: 30px; height: 30px; border: 0.5px solid var(--cc-on-chrome-faint, rgba(255,255,255,0.78)); border-radius: 8px; background: rgba(255,255,255,0.09); color: var(--cc-on-chrome-faint, rgba(255,255,255,0.78)); font-size: 14px; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: background 0.15s, color 0.15s; }
  .cc-auth-close:hover { background: rgba(255,255,255,0.18); color: var(--cc-on-chrome, #fff); }
  .cc-auth-logo { width: 140px; color: var(--cc-logo-on-chrome, #fff); display: flex; align-items: center; }
  .cc-auth-logo svg { width: 100%; height: auto; }
  .cc-auth-title { font-family: 'DM Serif Display', Georgia, serif; font-size: 22px; font-weight: 400; color: var(--cc-on-chrome, rgba(255,255,255,0.92)); margin: 0; line-height: 1.2; }
  .cc-auth-subtitle { font-family: 'DM Sans', system-ui, sans-serif; font-size: 13px; color: var(--cc-on-chrome-faint, rgba(255,255,255,0.55)); margin: -8px 0 0; }
  .cc-auth-body { display: flex; flex-direction: column; gap: 16px; padding: 24px 28px 0; border-top: 1px solid var(--cc-hairline-soft, rgba(0,58,143,0.06)); }
  .cc-auth-field { display: flex; flex-direction: column; gap: 6px; }
  .cc-auth-label { font-family: 'DM Sans', system-ui, sans-serif; font-size: 12px; font-weight: 600; color: var(--cc-ink-muted, #6B86B5); text-transform: uppercase; letter-spacing: 0.06em; }
  .cc-auth-input { width: 100%; box-sizing: border-box; min-height: 2.25rem; font-family: 'DM Sans', system-ui, sans-serif; font-size: 0.9375rem; color: var(--cc-ink, #1a1a1a); background: var(--cc-card, #ffffff); border: 1px solid var(--cc-edge, rgba(0,58,143,0.10)); border-radius: 12px; padding: 0.5rem 0.75rem; box-shadow: 0 1px 3px rgba(0,0,0,0.08), 0 0 0 1px rgba(0,58,143,0.04); transition: border-color 0.25s ease, box-shadow 0.25s ease, background 0.25s ease; appearance: none; -webkit-appearance: none; }
  .cc-auth-input:hover { border-color: var(--cc-edge, rgba(0,58,143,0.20)); box-shadow: 0 2px 6px rgba(0,0,0,0.1), 0 0 0 1px rgba(0,58,143,0.08); }
  .cc-auth-input:focus { outline: none; border-color: var(--cc-accent-fill, #003A8F); box-shadow: 0 0 0 4.5px var(--cc-focus-ring, rgba(0,58,143,0.18)), 0 2px 8px rgba(0,58,143,0.12); }
  .cc-auth-input::placeholder { color: var(--cc-ink-disabled, #a8adb8); }
  /* 🔴 A GLOBAL RULE STILL OUTRANKS THE ONE ABOVE ON EVERY TEXT FIELD, AND THAT HAS NOT CHANGED -
     only what it says has. public/styles.css sets input[type="text"], input[type="number"], select
     at specificity (0,1,1) against .cc-auth-input's (0,1,0), so it wins on every text and number
     field regardless of injection order. RUN GP found that the hard way: with the tokens above in
     force, First name / Last name / Referral code came back at 1.2:1 while Email and Password - the
     two types that selector does not list - took --cc-edge and passed at 3.62:1. Same class, same
     panel, two fields fixed and three not, and reading either copy of this file would have said the
     border was fixed. RUN GE fixed the global itself, so the two now agree by construction rather
     than by an override racing it. KEEP THE (0,1,1) IN MIND before writing another single-class rule
     for a field in this panel: it will not win. */
  /* RUN GE - THREE OF THE FOUR LINES THAT USED TO BE HERE ARE GONE, BECAUSE THEIR REASON IS GONE.
     public/styles.css now gives that same global selector --cc-edge, so the border/background/color
     overrides said exactly what the rule they were beating already says. A scoped override that
     outlives its cause is the next run's confusing finding: it reads as a claim that this panel
     needs something different, and re-deriving why it does not costs a measurement.
     The placeholder line STAYS, and it is not redundant: the global is
     input[type="text"]::placeholder (0,1,2), which beats .cc-auth-input::placeholder (0,1,1) and
     paints --cc-ink-muted, while the email and password fields - types that selector does not list -
     would keep --cc-ink-disabled. Both clear 4.5:1; they are different colours. Dropping this line
     would leave three fields with one placeholder ink and two with another, which is the same
     same-class-different-answer shape that hid the border defect from RUN GP in the first place. */
  .cc-auth-panel input.cc-auth-input::placeholder { color: var(--cc-ink-disabled, #a8adb8); }
  /* RUN FU's consent-row tap rule, carried into this copy so the two agree. FU has it inside a
     max-width:768px block in AccountChrome; unconditional here because a 44px consent row is right at
     every width and a media block for one declaration is not worth the divergence. */
  .cc-auth-agree { min-height: 44px; }
  .cc-auth-pw-wrap .cc-auth-input { padding-right: 40px; }
  .cc-auth-pw-wrap { position: relative; }
  .cc-auth-pw-toggle { position: absolute; right: 8px; top: 50%; transform: translateY(-50%); background: none; border: none; cursor: pointer; color: var(--cc-ink-muted, #6B86B5); padding: 6px; display: flex; }
  .cc-auth-primary { width: 100%; font-family: 'DM Sans', system-ui, sans-serif; font-size: 16px; font-weight: 600; padding: 14px 20px; border-radius: 10px; border: none; cursor: pointer; background: var(--cc-accent-fill, #003A8F); color: var(--cc-on-accent, #fff); transition: background 0.15s, transform 0.10s; display: flex; align-items: center; justify-content: center; }
  .cc-auth-primary:hover:not(:disabled) { background: var(--cc-accent-fill-hover, #002a6b); }
  .cc-auth-primary:active:not(:disabled) { transform: scale(0.99); }
  .cc-auth-primary:disabled { background: var(--cc-card-hover, rgba(0,58,143,0.12)); color: var(--cc-ink-disabled, rgba(0,42,107,0.62)); cursor: not-allowed; }
  .cc-auth-google { width: 100%; font-family: 'DM Sans', system-ui, sans-serif; font-size: 15px; font-weight: 500; padding: 12px 20px; border-radius: 10px; border: 1px solid var(--cc-edge, #dce4f2); cursor: pointer; background: var(--cc-card, #fff); color: var(--cc-ink, #1a2a4a); display: flex; align-items: center; justify-content: center; gap: 10px; transition: background 0.15s, border-color 0.15s; }
  .cc-auth-google:hover { background: var(--cc-card-hover, #f5f8ff); border-color: var(--cc-edge, #c5d4ed); }
  .cc-auth-divider { display: flex; align-items: center; gap: 12px; color: var(--cc-ink-muted, #5d6b80); font-family: 'DM Sans', system-ui, sans-serif; font-size: 12px; }
  .cc-auth-divider::before, .cc-auth-divider::after { content: ''; flex: 1; height: 1px; background: var(--cc-hairline, #e3ebf8); }
  .cc-auth-error-link { background: none; border: none; padding: 0; color: var(--cc-accent-text, #003A8F); font: inherit; font-weight: 600; cursor: pointer; text-decoration: underline; }
  .cc-auth-error { color: var(--cc-error-text, #b91c1c); font-family: 'DM Sans', system-ui, sans-serif; font-size: 13px; line-height: 1.5; text-align: center; }
  /* The failure grammar RUN P settled and QuietNote (packages/ui/.../QuietNote.jsx) owns: no box,
     no icon, no exclamation mark, no yellow; muted foreground, one line where possible, and the
     retry INLINE in the sentence rather than as a second button competing with the primary action.
     It is restated here rather than imported because this island is bundled by esbuild from
     apps/main/src and shares no module graph with packages/ui — the same reason the sign-up modal
     already exists twice (see the RUN O note below). Keep the two copies identical. */
  .cc-auth-note { color: var(--cc-ink-secondary, #6B86B5); font-family: 'DM Sans', system-ui, sans-serif; font-size: 13px; line-height: 1.5; text-align: center; }
  .cc-auth-note a { color: var(--cc-accent-text, #003A8F); font-weight: 600; }
  .cc-auth-retry { background: none; border: none; padding: 0; color: var(--cc-accent-text, #003A8F); font: inherit; font-weight: 600; cursor: pointer; text-decoration: underline; }
  .cc-auth-retry:disabled { color: var(--cc-ink-disabled, #9db0d0); cursor: default; text-decoration: none; }
  .cc-auth-forgot { background: none; border: none; padding: 0; color: var(--cc-accent-text, #6B86B5); font-family: 'DM Sans', system-ui, sans-serif; font-size: 12.5px; cursor: pointer; align-self: flex-end; margin-top: -2px; }
  .cc-auth-forgot:hover { color: var(--cc-accent-fill, #003A8F); text-decoration: underline; }
  .cc-auth-switch { font-family: 'DM Sans', system-ui, sans-serif; font-size: 13px; color: var(--cc-ink-secondary, #6B86B5); text-align: center; }
  .cc-auth-switch button { background: none; border: none; color: var(--cc-accent-text, #003A8F); font-weight: 600; cursor: pointer; font-size: 13px; padding: 0; }
  .cc-auth-link { background: none; border: none; color: var(--cc-accent-text, #003A8F); font-family: 'DM Sans', system-ui, sans-serif; font-size: 13px; font-weight: 500; cursor: pointer; padding: 0; }
  .cc-auth-back { background: none; border: none; color: var(--cc-ink-secondary, #6B86B5); font-family: 'DM Sans', system-ui, sans-serif; font-size: 13px; cursor: pointer; padding: 0; align-self: center; }
  .cc-auth-meta { font-family: 'DM Sans', system-ui, sans-serif; font-size: 13px; color: var(--cc-ink-secondary, #6B86B5); text-align: center; line-height: 1.5; }
  .cc-auth-meta strong { color: var(--cc-ink, #1a2a4a); font-weight: 600; }
  @keyframes cc-auth-spin { to { transform: rotate(360deg); } }
  `;
  document.head.appendChild(el);
}

function CiteClerkLogo() {
  return (
    <div className="cc-auth-logo" aria-hidden="true">
      <svg viewBox="106 357 1238 372" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g fill="currentColor">
          <path d="M137.46 717.89 c-9.78 -2.32 -17.98 -10.88 -19.57 -20.18 -1.47 -9.78 0.61 -13.57 19.93 -34.98 24.70 -27.39 27.39 -29.96 32.78 -30.94 5.87 -1.10 10.15 1.10 22.01 11.86 9.05 8.19 9.17 8.44 9.17 14.19 0 4.28 -0.73 6.97 -2.69 9.42 -7.83 10.15 -43.29 48.43 -46.23 50.02 -3.79 1.96 -9.17 2.20 -15.41 0.61z"/>
          <path d="M194.45 635.33 c-6.36 -5.63 -11.74 -10.76 -11.86 -11.25 -0.24 -0.61 1.71 -3.18 4.40 -5.75 l4.65 -4.53 9.05 7.83 c5.01 4.40 10.64 9.29 12.60 11.01 l3.67 2.94 -4.04 5.14 c-2.20 2.69 -4.65 5.01 -5.38 4.89 -0.86 0 -6.73 -4.65 -13.09 -10.27z"/>
          <path d="M277.86 644.51 c-20.42 -2.94 -41.58 -10.76 -57.97 -21.52 -11.74 -7.70 -32.04 -28.01 -39.99 -39.99 -14.19 -21.16 -22.14 -47.70 -22.14 -73.38 0 -56.75 40.48 -110.80 95.88 -127.92 46.60 -14.43 98.20 -2.57 130.98 30.09 8.93 8.93 20.18 23.11 20.18 25.32 0 1.22 -12.47 10.40 -28.74 21.04 l-4.77 3.18 -3.06 -4.04 c-5.75 -7.95 -21.65 -22.50 -29.47 -27.03 -4.28 -2.57 -12.23 -6.11 -17.73 -7.95 -8.68 -3.06 -11.50 -3.42 -25.07 -3.42 -32.04 0 -49.16 6.97 -70.93 28.74 -8.44 8.32 -11.50 12.47 -15.53 20.79 -7.58 15.41 -9.42 23.73 -9.42 40.97 0 16.88 1.96 25.44 8.93 40.36 8.68 18.47 23.60 33.63 42.31 42.80 13.57 6.73 22.26 9.05 37.30 9.91 19.93 1.22 40.97 -5.01 57.97 -17.12 8.07 -5.75 19.93 -17.86 23.60 -23.97 1.10 -1.96 2.57 -3.67 2.94 -3.67 0.49 0 8.68 5.01 18.10 11.25 19.57 12.72 18.83 10.76 8.44 23.85 -6.11 7.70 -28.62 29.72 -30.33 29.72 -0.61 0 -4.28 2.20 -8.32 4.77 -4.04 2.69 -8.68 5.26 -10.52 5.75 -1.71 0.37 -3.55 1.22 -3.91 1.83 -0.37 0.61 -1.47 1.10 -2.32 1.10 -0.98 0 -4.65 1.35 -8.19 2.94 -10.76 4.77 -42.31 7.83 -58.21 5.63z"/>
          <path d="M644.51 618.82 c-21.28 -3.91 -39.01 -17.37 -47.94 -36.32 -10.64 -22.50 -8.44 -49.78 5.63 -70.44 13.57 -19.81 39.14 -30.57 62.98 -26.54 30.09 5.01 50.26 31.43 50.26 65.67 l0 10.15 -45.86 0 c-51.61 0 -49.04 -0.49 -45.37 9.17 2.69 6.85 9.66 14.06 17.37 17.86 4.89 2.32 7.83 2.94 15.78 2.94 8.44 -0.12 10.64 -0.61 16.27 -3.67 3.55 -1.96 8.19 -5.38 10.15 -7.70 2.08 -2.32 4.40 -3.79 5.26 -3.55 0.98 0.37 6.48 3.79 12.35 7.58 l10.52 6.73 -3.55 4.65 c-6.85 8.44 -13.09 13.21 -22.26 17.24 -5.01 2.20 -10.27 4.53 -11.62 5.38 -3.06 1.71 -22.50 2.32 -29.96 0.86z m39.62 -84.02 c-5.38 -23.24 -36.08 -31.19 -52.59 -13.57 -3.79 3.91 -9.05 13.09 -9.05 15.65 0 0.86 10.40 1.22 31.19 1.22 l31.19 0 -0.73 -3.30z"/>
          <path d="M805.94 618.21 c-34.73 -7.70 -61.27 -32.78 -71.18 -67.39 -2.20 -7.83 -2.81 -12.60 -2.81 -25.56 0 -18.59 2.57 -29.47 10.52 -44.64 11.99 -23.24 31.55 -39.87 56.38 -48.19 13.21 -4.53 38.89 -5.75 52.83 -2.69 14.80 3.30 30.21 11.74 40.48 22.01 4.65 4.77 9.66 10.76 11.01 13.33 l2.45 4.77 -2.45 1.71 c-6.24 4.53 -25.07 16.39 -26.05 16.39 -0.61 0 -1.83 -1.47 -2.81 -3.18 -4.53 -8.56 -20.79 -19.57 -33.02 -22.38 -8.19 -1.96 -21.04 -1.71 -23.85 0.49 -1.22 0.98 -3.55 1.83 -5.01 1.83 -7.22 0.12 -24.34 10.27 -30.82 18.34 -8.68 10.88 -14.80 27.88 -14.80 41.58 0 15.65 6.85 33.02 17.37 44.39 18.83 20.18 47.33 24.83 72.16 11.62 6.85 -3.67 11.13 -7.46 18.34 -16.88 3.30 -4.16 3.42 -4.28 6.73 -2.32 16.75 9.91 24.83 15.04 24.83 15.65 0 0.37 -2.81 4.40 -6.24 8.93 -9.54 12.72 -24.83 24.70 -35.34 27.76 -3.06 0.86 -7.09 2.45 -9.17 3.67 -5.14 2.69 -38.16 3.30 -49.53 0.73z"/>
          <path d="M1028.89 618.70 c-14.55 -2.45 -30.33 -11.13 -39.50 -21.65 -6.36 -7.34 -12.84 -20.79 -14.80 -30.57 -1.96 -10.27 -0.73 -28.13 2.45 -37.42 7.09 -20.30 22.38 -35.34 41.95 -41.21 6.97 -2.20 10.76 -2.45 22.75 -2.08 13.09 0.37 15.04 0.73 22.50 4.28 21.28 10.03 33.39 30.45 34.85 58.83 l0.73 12.47 -44.64 0 c-51.49 0 -50.51 -0.24 -46.47 8.81 3.30 7.58 10.27 14.92 17.12 18.34 8.68 4.16 21.77 3.91 31.06 -0.61 3.79 -1.83 8.68 -5.38 10.76 -7.70 2.08 -2.45 4.16 -4.16 4.65 -3.91 0.49 0.24 5.87 3.30 12.11 6.97 l11.25 6.73 -2.08 3.55 c-3.42 5.75 -15.16 15.90 -22.87 19.69 -13.57 6.60 -25.68 8.19 -41.83 5.50z m38.77 -83.04 c0 -6.48 -8.44 -17.49 -16.14 -21.16 -17 -8.07 -38.52 1.71 -43.78 19.69 l-1.10 3.91 30.57 0 c29.84 0 30.45 0 30.45 -2.45z"/>
          <path d="M539.70 616.50 c-11.01 -3.79 -19.08 -11.62 -23.11 -22.26 -1.71 -4.40 -2.20 -11.74 -2.69 -42.31 l-0.49 -37.06 -10.88 0 -10.88 0 0 -13.45 0 -13.45 11.01 0 11.01 0 0 -17.73 0 -17.73 16.51 0 16.39 0 0.37 17.49 0.37 17.37 15.65 0.37 15.65 0.37 -0.37 13.70 -0.37 13.70 -13.94 0.37 c-8.44 0.24 -14.68 0.86 -15.78 1.71 -1.35 1.22 -1.59 6.85 -1.22 31.67 0.37 28.37 0.49 30.45 2.94 33.75 1.35 1.83 4.77 4.16 7.46 5.14 4.28 1.59 5.87 1.59 12.47 -0.12 4.04 -0.98 7.46 -1.71 7.58 -1.71 0 0.12 0.73 5.63 1.71 12.35 1.83 13.82 1.59 14.31 -6.11 17.86 -6.73 3.06 -24.46 3.06 -33.26 0z"/>
          <path d="M441.49 552.17 l0 -64.21 17.12 0 17.12 0 -0.24 63.96 -0.37 63.84 -16.75 0.37 -16.88 0.37 0 -64.33z"/>
          <path d="M920.90 523.43 l0 -92.95 17.12 0 17.12 0 0 92.95 0 92.95 -17.12 0 -17.12 0 0 -92.95z"/>
          <path d="M1117.80 552.17 l0 -64.21 16.51 0 16.51 0 0.12 8.93 c0 8.68 0 8.81 2.08 5.75 2.94 -4.53 9.78 -10.76 13.94 -12.96 6.85 -3.55 16.75 -5.50 22.75 -4.40 l5.75 0.86 0.37 15.53 0.37 15.53 -8.44 -0.73 c-15.29 -1.22 -26.54 4.77 -32.90 17.73 l-3.42 6.97 -0.37 37.67 -0.49 37.55 -16.39 0 -16.39 0 0 -64.21z"/>
          <path d="M1214.05 615.52 c-0.49 -0.37 -0.86 -42.19 -0.86 -92.95 l0 -92.09 17.12 0 17.12 0 0.24 53.20 0.37 53.08 17.49 -20.42 c9.54 -11.25 19.08 -22.26 21.04 -24.46 l3.67 -3.91 19.45 0.24 19.32 0.37 -19.45 22.01 c-10.76 12.11 -20.91 23.85 -22.63 26.05 l-3.18 3.91 4.65 7.09 c2.57 3.79 13.94 20.55 25.19 37.18 11.37 16.63 20.67 30.45 20.67 30.82 0 0.37 -8.68 0.73 -19.20 0.73 l-19.32 0 -16.51 -25.19 c-9.05 -13.94 -17.12 -25.56 -18.10 -25.80 -0.98 -0.37 -4.28 1.83 -7.70 5.26 l-5.99 5.87 0 19.93 0 19.93 -16.27 0 c-9.05 0 -16.75 -0.37 -17.12 -0.86z"/>
          <path d="M449.08 466.32 c-10.15 -5.01 -14.31 -18.47 -8.68 -28.50 5.87 -10.27 20.18 -14.43 29.23 -8.19 6.36 4.28 8.93 8.56 9.54 16.14 0.73 8.32 -1.10 12.72 -7.22 18.34 -4.28 3.91 -5.50 4.28 -11.86 4.28 -3.91 0 -8.68 -0.98 -11.01 -2.08z"/>
        </g>
      </svg>
    </div>
  );
}

function Spinner() {
  return (
    <span style={{
      display: "inline-block", width: 14, height: 14,
      border: "2px solid rgba(255,255,255,0.35)", borderTopColor: "#fff",
      borderRadius: "50%", animation: "cc-auth-spin 0.7s linear infinite",
      marginRight: 8, flexShrink: 0,
    }} aria-hidden="true" />
  );
}

function GoogleIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  );
}

// B-HOME-4: errors in OUR voice — Clerk error CODES map to CiteClerk copy; raw Clerk
// strings never reach the user. `offerSignIn` marks the taken-email case so the
// sign-up pane can offer the switch to sign-in.
const ERR_COPY = {
  form_identifier_exists: { msg: "You already have a CiteClerk account with this email.", offerSignIn: true },
  form_password_pwned: { msg: "That password has appeared in a known data breach — please choose a different one." },
  form_password_length_too_short: { msg: "Passwords need at least 8 characters." },
  form_password_not_strong_enough: { msg: "That password is too easy to guess — try something longer." },
  form_password_validation_failed: { msg: "That password doesn\u2019t meet the requirements — try something longer." },
  form_code_incorrect: { msg: "That code doesn\u2019t match — check the email and try again." },
  verification_expired: { msg: "That code has expired — resend a fresh one below." },
  verification_failed: { msg: "Verification couldn\u2019t be completed — resend a fresh code and try again." },
  form_identifier_not_found: { msg: "We can\u2019t find a CiteClerk account with that email." },
  form_password_incorrect: { msg: "That password isn\u2019t right — try again." },
  session_exists: { msg: "You\u2019re already signed in." },
  // \u2500\u2500 Password reset \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500
  // `strategy_for_user_invalid` is what Clerk answers when the account has no password factor at
  // all \u2014 a Google-only account. "That code didn\u2019t match" would be a lie about a code that was
  // never sent, and would send the user hunting through an inbox for nothing.
  strategy_for_user_invalid: { msg: "That account doesn\u2019t use a password \u2014 use \u201cContinue with Google\u201d instead." },
  form_password_incorrect_or_expired: { msg: "That reset code has expired \u2014 send a fresh one." },
};

// \ud83d\udd34 SIGNED OUT GETS ITS OWN SENTENCE. Clerk answers an expired or absent session with an ordinary
// 401, and "something went wrong on our end" for a user whose only problem is a lapsed session is
// the least actionable copy in the product. The account panel already follows this rule
// (`loadFailureText`, packages/ui/react/AccountPanel/readJson.js); it is restated here because this
// esbuild island shares no module graph with packages/ui.
const isSignedOutError = (e) => {
  const code = e?.errors?.[0]?.code;
  return e?.status === 401 || code === "authentication_invalid" || code === "session_not_found";
};

const fmtErr = (e) => {
  if (e && e.__cc) return { msg: e.__cc };
  const code = e?.errors?.[0]?.code;
  if (code && ERR_COPY[code]) return ERR_COPY[code];
  if (isSignedOutError(e)) return { msg: "You\u2019re signed out \u2014 sign in again to continue." };
  if (!e?.errors && e?.message && /fetch|network|load failed/i.test(e.message))
    return { msg: "We couldn\u2019t reach CiteClerk — check your connection and try again." };
  return { msg: "Something went wrong on our end. Please try again." };
};

// ── Sign-in flow (inside ClerkProvider) ──
function SignInFlow({ afterUrl, close, onSwitchToSignUp }) {
  const { isLoaded, signIn, setActive } = useSignIn();
  // 🔴 `reset` AND `reset_code` ARE NEW. There was no way to recover a forgotten password anywhere
  // in the product: the modal offered email, password, Google and "Sign up", and nothing else. A
  // user who forgot their password had no path back to their account at all — not a degraded one,
  // none. Clerk supports this natively and the production instance advertises the factor
  // (`first_factors` includes `reset_password_email_code`), so this drives Clerk's flow rather than
  // inventing a parallel one.
  const [step, setStep] = useState("credentials"); // credentials | reset | reset_code | code | second | done
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [secondCode, setSecondCode] = useState("");
  const [secondStrategy, setSecondStrategy] = useState("email_code"); // email_code | phone_code | totp
  const [secondTarget, setSecondTarget] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [emailFactorId, setEmailFactorId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Password reset state.
  const [resetCode, setResetCode] = useState("");
  const [newPw, setNewPw] = useState("");
  const [showNewPw, setShowNewPw] = useState(false);
  const [resetSent, setResetSent] = useState(false); // a code is outstanding → offer a resend

  const fail = (e) => { setError(fmtErr(e)); setLoading(false); };
  const done = async (sessionId) => {
    await setActive({ session: sessionId });
    setStep("done");
    window.location.href = afterUrl || "/citations";
  };

  // Route a needs_second_factor result to the second-factor step.
  // Client Trust surfaces here as an email_code factor on new devices.
  const routeSecondFactor = async (res) => {
    if (res.status !== "needs_second_factor") return false;
    const factors = res.supportedSecondFactors || [];
    const totp = factors.find((f) => f.strategy === "totp");
    const emailF = factors.find((f) => f.strategy === "email_code");
    const phone = factors.find((f) => f.strategy === "phone_code");

    if (totp) {
      // Account has an authenticator app enrolled — no prepare needed.
      setSecondStrategy("totp");
    } else if (emailF) {
      // Client Trust: emailed 6-digit code.
      setSecondStrategy("email_code");
      setSecondTarget(emailF.safeIdentifier || email);
      try { await signIn.prepareSecondFactor({ strategy: "email_code", emailAddressId: emailF.emailAddressId }); }
      catch (e) { console.info("[auth] email_code prepare note:", fmtErr(e)); }
    } else if (phone) {
      setSecondStrategy("phone_code");
      setSecondTarget(phone.safeIdentifier || "");
      try { await signIn.prepareSecondFactor({ strategy: "phone_code", phoneNumberId: phone.phoneNumberId }); }
      catch (e) { console.info("[auth] phone_code prepare note:", fmtErr(e)); }
    } else {
      return false;
    }
    setSecondCode("");
    setStep("second"); setLoading(false);
    return true;
  };

  const submitCredentials = async () => {
    if (!isLoaded || !email) return;
    setLoading(true); setError(null);
    try {
      const created = await signIn.create({ identifier: email });
      if (created.status === "complete") return done(created.createdSessionId);

      const factors = created.supportedFirstFactors || [];
      const hasPw = factors.some((f) => f.strategy === "password");

      if (hasPw) {
        if (!password) { setLoading(false); setError("Enter your password."); return; }
        const res = await signIn.attemptFirstFactor({ strategy: "password", password });
        console.info("[auth] status after password:", res.status);
        if (await routeSecondFactor(res)) return;   // Client Trust email code / MFA
        if (res.status === "complete") return done(res.createdSessionId);
        fail({ __cc: "Sign-in couldn\u2019t be completed — please try again." });
      } else {
        // Instance has no password factor → passwordless email-code (first factor)
        const ec = factors.find((f) => f.strategy === "email_code");
        if (!ec) return fail({ __cc: "This account doesn\u2019t have a sign-in method we can use here — try \u201cContinue with Google\u201d." });
        await signIn.prepareFirstFactor({ strategy: "email_code", emailAddressId: ec.emailAddressId });
        setEmailFactorId(ec.emailAddressId);
        setStep("code"); setLoading(false);
      }
    } catch (e) { fail(e); }
  };

  // First-factor email code (passwordless instances only)
  const submitCode = async () => {
    if (!code) return;
    setLoading(true); setError(null);
    try {
      const res = await signIn.attemptFirstFactor({ strategy: "email_code", code });
      if (await routeSecondFactor(res)) return;
      if (res.status === "complete") return done(res.createdSessionId);
      fail({ errors: [{ code: "verification_failed" }] });
    } catch (e) { fail(e); }
  };

  // Second factor (Client Trust email code, SMS, or authenticator)
  const submitSecond = async () => {
    if (!secondCode) return;
    setLoading(true); setError(null);
    try {
      const res = await signIn.attemptSecondFactor({ strategy: secondStrategy, code: secondCode });
      if (res.status === "complete") return done(res.createdSessionId);
      fail({ errors: [{ code: "verification_failed" }] });
    } catch (e) { fail(e); }
  };

  const resendFirstCode = async () => {
    setError(null);
    try { await signIn.prepareFirstFactor({ strategy: "email_code", emailAddressId: emailFactorId }); }
    catch (e) { setError(fmtErr(e)); }
  };

  const resendSecond = async () => {
    setError(null);
    try {
      if (secondStrategy === "email_code") await signIn.prepareSecondFactor({ strategy: "email_code" });
      else if (secondStrategy === "phone_code") await signIn.prepareSecondFactor({ strategy: "phone_code" });
    } catch (e) { setError(fmtErr(e)); }
  };

  // ── Password reset ────────────────────────────────────────────────────────────────────────────
  // Clerk's native flow, in two calls: `create({strategy:'reset_password_email_code'})` emails the
  // code; `attemptFirstFactor` with the code AND the new password sets it and, on this instance,
  // returns `complete` with a session. A reset that lands on `needs_second_factor` (2FA accounts)
  // routes through the SAME `routeSecondFactor` the password path uses, so the two cannot drift.
  const startReset = async ({ resend = false } = {}) => {
    if (!isLoaded) return;
    if (!email) { setError({ msg: "Enter your email address first." }); return; }
    setLoading(true); setError(null);
    try {
      await signIn.create({ strategy: "reset_password_email_code", identifier: email });
      setResetSent(true);
      setStep("reset_code");
      if (resend) setError(null);
    } catch (e) { fail(e); return; }
    setLoading(false);
  };

  const submitReset = async () => {
    if (resetCode.length < 6 || !newPw) return;
    setLoading(true); setError(null);
    try {
      const res = await signIn.attemptFirstFactor({
        strategy: "reset_password_email_code", code: resetCode, password: newPw,
      });
      // 🔴 A NON-`complete` STATUS IS NOT A FAILURE OF THE CODE, AND MUST NOT BE REPORTED AS ONE.
      // `needs_second_factor` means the code was accepted and the password was changed; the account
      // simply has 2FA. Telling that user "that code didn't match" would be false about the code
      // they just typed correctly — the same shape of mistake that made sign-up unrecoverable.
      if (await routeSecondFactor(res)) return;
      if (res.status === "complete") return done(res.createdSessionId);
      fail({ __cc: `Your password was not changed — the reset ended in an unexpected state (${res.status}). Send a fresh code and try again.` });
    } catch (e) { fail(e); }
  };

  const google = async () => {
    if (!isLoaded) return;
    setError(null);
    try {
      await signIn.authenticateWithRedirect({
        strategy: "oauth_google",
        redirectUrl: window.location.origin + "/citations",
        redirectUrlComplete: afterUrl || "/citations",
      });
    } catch (e) { fail(e); }
  };

  const onKey = (fn) => (e) => { if (e.key === "Enter") { e.preventDefault(); fn(); } };

  if (step === "done") {
    return (
      <div style={{ textAlign: "center", padding: "20px 0 10px" }}>
        <div style={{ width: 52, height: 52, borderRadius: "50%", background: "rgba(0,58,143,0.10)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 18px" }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--cc-accent-text, #003A8F)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
        </div>
        <div style={{ fontFamily: "'DM Serif Display', Georgia, serif", fontSize: 22, color: "var(--cc-ink, #1a2a4a)", marginBottom: 10 }}>You're in</div>
        <div className="cc-auth-meta">Taking you to the app…</div>
      </div>
    );
  }

  if (step === "second") {
    const meta =
      secondStrategy === "email_code"
        ? <>We emailed a 6-digit code to <strong>{secondTarget || email}</strong></>
        : secondStrategy === "phone_code"
        ? <>We texted a 6-digit code to <strong>{secondTarget}</strong></>
        : <>Enter the code from your authenticator app</>;
    const canResend = secondStrategy === "email_code" || secondStrategy === "phone_code";
    return (
      <>
        <div className="cc-auth-meta">{meta}</div>
        <div className="cc-auth-field">
          <label className="cc-auth-label" htmlFor="cc-2fa">Verification code</label>
          <input id="cc-2fa" className="cc-auth-input" type="text" inputMode="numeric"
            value={secondCode} autoFocus placeholder="Enter the 6-digit code"
            onChange={(e) => setSecondCode(e.target.value.replace(/\D/g, "").slice(0, 8))}
            onKeyDown={onKey(submitSecond)} />
        </div>
        {error && <div className="cc-auth-error">{error.msg || error}</div>}
        <button className="cc-auth-primary" disabled={loading || secondCode.length < 6} onClick={submitSecond}>
          {loading ? <><Spinner/>Verifying…</> : "Verify"}
        </button>
        {canResend && <button className="cc-auth-back" onClick={resendSecond} disabled={loading}>Resend code</button>}
      </>
    );
  }

  // ── Reset step 1: which account ───────────────────────────────────────────────────────────────
  if (step === "reset") {
    return (
      <>
        <div className="cc-auth-meta">Enter your email address and we'll send you a code to set a new password.</div>
        <div className="cc-auth-field">
          <label className="cc-auth-label" htmlFor="cc-reset-email">Email address</label>
          <input id="cc-reset-email" className="cc-auth-input" type="email" value={email} autoFocus
            placeholder="you@firm.com" onChange={(e) => { setEmail(e.target.value); setError(null); }}
            onKeyDown={onKey(startReset)} />
        </div>
        {error && <div className="cc-auth-note">{error.msg || error}</div>}
        <button className="cc-auth-primary" disabled={loading || !email} onClick={() => startReset()}>
          {loading ? <><Spinner/>Sending…</> : "Send reset code"}
        </button>
        <button className="cc-auth-back" onClick={() => { setStep("credentials"); setError(null); }}>Back to sign in</button>
      </>
    );
  }

  // ── Reset step 2: the code and the new password, on one screen ────────────────────────────────
  // Deliberately one screen: splitting them means a user who mistypes the password has to ask for a
  // second code, and the first one is still live — two valid codes and no way to tell which is which.
  if (step === "reset_code") {
    return (
      <>
        <div className="cc-auth-meta">We emailed a 6-digit code to <strong>{email}</strong></div>
        <div className="cc-auth-field">
          <label className="cc-auth-label" htmlFor="cc-reset-code">Reset code</label>
          <input id="cc-reset-code" className="cc-auth-input" type="text" inputMode="numeric"
            value={resetCode} autoFocus placeholder="Enter the 6-digit code"
            onChange={(e) => { setResetCode(e.target.value.replace(/\D/g, "").slice(0, 6)); setError(null); }}
            onKeyDown={onKey(submitReset)} />
        </div>
        <div className="cc-auth-field">
          <label className="cc-auth-label" htmlFor="cc-reset-pw">New password</label>
          <div className="cc-auth-pw-wrap">
            <input id="cc-reset-pw" className="cc-auth-input" type={showNewPw ? "text" : "password"}
              value={newPw} placeholder="Choose a new password" autoComplete="new-password"
              onChange={(e) => { setNewPw(e.target.value); setError(null); }} onKeyDown={onKey(submitReset)} />
            <button type="button" className="cc-auth-pw-toggle"
              aria-label={showNewPw ? "Hide password" : "Show password"}
              onClick={() => setShowNewPw((s) => !s)}>
              {showNewPw ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z"/><circle cx="12" cy="12" r="3"/></svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
              )}
            </button>
          </div>
        </div>
        {/* The failure grammar: muted, one line, and the retry inline in the sentence. */}
        {error && (
          <div className="cc-auth-note">
            {error.msg || error}{" "}
            {resetSent && (
              <button type="button" className="cc-auth-retry" disabled={loading} onClick={() => startReset({ resend: true })}>
                {loading ? "Sending…" : "Send a new code"}
              </button>
            )}
          </div>
        )}
        <button className="cc-auth-primary" disabled={loading || resetCode.length < 6 || !newPw} onClick={submitReset}>
          {loading ? <><Spinner/>Setting password…</> : "Set new password"}
        </button>
        <button className="cc-auth-back" onClick={() => { setStep("credentials"); setError(null); }}>Back to sign in</button>
      </>
    );
  }

  if (step === "code") {
    return (
      <>
        <div className="cc-auth-meta">We sent a code to <strong>{email}</strong></div>
        <div className="cc-auth-field">
          <label className="cc-auth-label" htmlFor="cc-code">Verification code</label>
          <input id="cc-code" className="cc-auth-input" type="text" inputMode="numeric"
            value={code} autoFocus placeholder="Enter the 6-digit code"
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            onKeyDown={onKey(submitCode)} />
        </div>
        {error && <div className="cc-auth-error">{error.msg || error}</div>}
        <button className="cc-auth-primary" disabled={loading || code.length < 6} onClick={submitCode}>
          {loading ? <><Spinner/>Verifying…</> : "Verify"}
        </button>
        <button className="cc-auth-back" onClick={resendFirstCode} disabled={loading}>Resend code</button>
      </>
    );
  }

  // step === "credentials" — email + password on top, Google below
  return (
    <>
      <div className="cc-auth-field">
        <label className="cc-auth-label" htmlFor="cc-email">Email address</label>
        <input id="cc-email" className="cc-auth-input" type="email" value={email} autoFocus
          placeholder="you@firm.com" onChange={(e) => setEmail(e.target.value)} onKeyDown={onKey(submitCredentials)} />
      </div>
      <div className="cc-auth-field">
        <label className="cc-auth-label" htmlFor="cc-pw">Password</label>
        <div className="cc-auth-pw-wrap">
          <input id="cc-pw" className="cc-auth-input" type={showPw ? "text" : "password"}
            value={password} placeholder="Enter your password"
            onChange={(e) => setPassword(e.target.value)} onKeyDown={onKey(submitCredentials)} />
          <button type="button" className="cc-auth-pw-toggle"
            aria-label={showPw ? "Hide password" : "Show password"}
            onClick={() => setShowPw((s) => !s)}>
            {showPw ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z"/><circle cx="12" cy="12" r="3"/></svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
            )}
          </button>
        </div>
        {/* The recovery path. It sits under the password field, where a user who has just failed to
            remember it is already looking. */}
        <button type="button" className="cc-auth-forgot"
          onClick={() => { setStep("reset"); setError(null); setResetCode(""); setNewPw(""); setResetSent(false); }}>
          Forgot password?
        </button>
      </div>
      {error && <div className="cc-auth-error">{error.msg || error}</div>}
      <button className="cc-auth-primary" disabled={loading || !email || !password} onClick={submitCredentials}>
        {loading ? <><Spinner/>Signing in…</> : "Sign in"}
      </button>
      <div className="cc-auth-divider">or</div>
      <button className="cc-auth-google" onClick={google} disabled={loading}>
        <GoogleIcon /> Continue with Google
      </button>
      <div className="cc-auth-switch">
        Don't have an account?{" "}
        <button onClick={onSwitchToSignUp}>Sign up</button>
      </div>
    </>
  );
}

// ── Sign-up flow (inside ClerkProvider) ──
// ═══════════════════════════════════════════════════════════════════════════════════════════════
// RUN O — THE REFERRAL CODE IS NOW CHECKED AGAINST THE SERVER AND ITS CAPTURE IS OBSERVED.
//
// What this replaces, verbatim from the comment that used to sit in `done()`:
//
//     "The referral backend is LIVE but on the STEEL origin (POST /api/referrals/capture, post-auth)
//      and apps/main does NOT yet proxy /api/referrals/* → this call 404s until Adam wires that
//      proxy; the stash lets a post-auth step apply it then."
//
// So the flow POSTed into a 404, ignored the result, wrote the code to localStorage for a
// "post-auth step" that does not exist, and navigated to /citations. A user who typed a perfectly
// good code — or a typo — saw an identical, successful-looking signup either way. The format regex
// below could only ever catch a code shaped wrong; it could not catch one that simply does not exist,
// which is the entire population of realistic mistakes.
//
// Now: /api/referrals/check (unauthenticated, on main's own origin) answers BEFORE the account is
// created, and /api/referrals/capture returns a rendered message if it fails afterwards. The
// localStorage stash is kept ONLY as a retry hint for a capture that failed after the account
// existed — a case where the user is now signed in and the Referrals pane can finish the job.
// ═══════════════════════════════════════════════════════════════════════════════════════════════
// Shape gate only — 6–14 of A–Z/2–9. The AUTHORITY on whether a code exists is the server.
// 🔴 RUN AI — BOTH CODE SHAPES, AND THE DIGIT HALF IS WHY THIS HAD TO CHANGE.
//
// This was `/^[A-Za-z2-9]{6,14}$/`. That class deliberately excludes 0 and 1 as visually
// confusable — correct for the retired random format, and WRONG the moment a code ends in four
// decimal digits. `LACHES0001` fails it on its last two characters.
//
// This regex gates the signup modal BEFORE anything is sent to /api/referrals/check, so leaving it
// alone would have refused most real codes client-side with "that doesn't look like a referral
// code" — a validation error, on a valid code, that never reaches a server log. Roughly 1 - 0.8^4
// = 59% of minted codes contain at least one 0 or 1.
//
// Kept deliberately in sync with CODE_RE in @citeclerk/plan/referral. It is not imported because
// this file ships to the browser in three apps and the plan package is CJS + server-side; the
// duplication is covered by referralCodeShape.test.mjs, which reads BOTH files and fails if they
// diverge.
const REFERRAL_RE = /^(?:[A-Za-z]{4,10}[0-9]{4}|[A-Za-z2-9]{6,14})$/;

/** The code stamped into the page by GET /referral/<CODE>, or the ?ref= query fallback. */
function readReferralFromPage() {
  try {
    const stamped = typeof window !== "undefined" ? window.__CC_REFERRAL_CODE__ : null;
    if (stamped) return { code: String(stamped).toUpperCase(), invalid: false };
    const q = new URLSearchParams(window.location.search).get("ref");
    if (q) return { code: String(q).trim().toUpperCase(), invalid: false };
  } catch { /* no window / bad URL */ }
  return { code: "", invalid: !!(typeof window !== "undefined" && window.__CC_REFERRAL_INVALID__) };
}

function SignUpFlow({ afterUrl, close, onSwitchToSignIn }) {
  const { isLoaded, signUp, setActive } = useSignUp();
  const [step, setStep] = useState("form"); // form | verify | done
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [agree, setAgree] = useState(false);
  const [code, setCode] = useState("");
  const prefill = readReferralFromPage();
  const [referral, setReferral] = useState(prefill.code);
  const [referralErr, setReferralErr] = useState(
    prefill.invalid ? "That referral link doesn't contain a valid code. You can sign up without one." : "");
  const [referralOk, setReferralOk] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /** Ask the SERVER whether the code exists. Returns true when it does. Never throws. */
  const checkReferral = useCallback(async (raw) => {
    const rc = String(raw || "").trim().toUpperCase();
    setReferralOk(false);
    if (!rc) { setReferralErr(""); return false; }
    if (!REFERRAL_RE.test(rc)) { setReferralErr("That doesn't look like a referral code — they're a word followed by four digits, like CERTIORARI4417."); return false; }
    try {
      const r = await fetch("/api/referrals/check", {
        method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ code: rc }),
      });
      const j = await r.json().catch(() => ({}));
      if (r.ok && j.ok) { setReferralErr(""); setReferralOk(true); return true; }
      // A 503 is "we could not check", NOT "invalid" — the server sends copy that says which.
      setReferralErr(j.message || "We don't recognise that referral code.");
      return false;
    } catch {
      setReferralErr("We couldn't check that code just now — you can add it later from Referrals.");
      return false;
    }
  }, []);

  // Validate a prefilled code as soon as the modal opens, so a dead link says so on arrival rather
  // than after the account exists.
  useEffect(() => { if (prefill.code) checkReferral(prefill.code); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fail = (e) => { setError(fmtErr(e)); setLoading(false); };
  const done = async (sessionId) => {
    await setActive({ session: sessionId });
    // A referral code NEVER blocks signup — but a capture that FAILS must not render as success.
    // The code was already validated against the server before the account was created; this is the
    // attach, and its result is kept (the stash is now only a retry hint for the Referrals pane).
    const rc = referral.trim().toUpperCase();
    if (rc && REFERRAL_RE.test(rc)) {
      let attached = false;
      try {
        const r = await fetch("/api/referrals/capture", {
          method: "POST", credentials: "include",
          headers: { "content-type": "application/json" }, body: JSON.stringify({ code: rc }),
        });
        const j = await r.json().catch(() => ({}));
        attached = !!(r.ok && j.ok);
        if (!attached) {
          // The account exists and the referral did not attach. Say so on the next screen rather
          // than navigating away from a failure nobody saw.
          try { sessionStorage.setItem("cc_referral_notice", j.message || "We couldn't attach that referral code."); } catch { /* */ }
        }
      } catch {
        try { sessionStorage.setItem("cc_referral_notice", "We couldn't attach that referral code — try again from Referrals."); } catch { /* */ }
      }
      // Retry hint for the Referrals pane; cleared once the attach has succeeded.
      try {
        if (attached) localStorage.removeItem("cc_pending_referral");
        else localStorage.setItem("cc_pending_referral", rc);
      } catch { /* */ }
    }
    setStep("done");
    window.location.href = afterUrl || "/citations";
  };

  const submitForm = async () => {
    if (!isLoaded) return;
    if (!first || !last || !email || !password) { setError("Please fill in all fields."); return; }
    if (!agree) { setError("Please agree to the Terms of Service and Privacy Policy."); return; }
    // Referral is optional and NON-blocking: a code the server does not recognise shows the field
    // error but never stops account creation. The check is AWAITED so the message is on screen
    // before the account is made — the previous version could not tell an unknown code from a good
    // one at all, and neither could the user.
    const rc = referral.trim();
    if (rc) await checkReferral(rc);
    setLoading(true); setError(null);
    try {
      // ═══════════════════════════════════════════════════════════════════════════════════════
      // 🔴 `legalAccepted` — THE FIELD WHOSE ABSENCE MADE EVERY PRODUCTION SIGN-UP UNFINISHABLE.
      //
      // The production Clerk instance has legal consent ON (`user_settings.sign_up
      // .legal_consent_enabled: true`); the development instance has it OFF. Nothing in this
      // repo has ever sent `legalAccepted` — the checkbox above gates our own submit button and
      // was never forwarded to Clerk.
      //
      // On dev that was invisible. On prod it means `signUp.create()` returns
      // `missing_requirements` with `legal_accepted` outstanding, and the requirement is never
      // met — so the emailed code verifies FINE and the SignUp still cannot reach `complete`.
      // The user sees a six-digit code arrive, types it correctly, and is told verification
      // failed. The code was never the problem; the account was missing a field nobody sent.
      // ═══════════════════════════════════════════════════════════════════════════════════════
      const created = await signUp.create({
        firstName: first, lastName: last, emailAddress: email, password, legalAccepted: agree,
      });
      if (created.status === "complete") return done(created.createdSessionId);
      await signUp.prepareEmailAddressVerification({ strategy: "email_code" });
      setStep("verify"); setLoading(false);
    } catch (e) { fail(e); }
  };

  const submitCode = async () => {
    if (!code) return;
    setLoading(true); setError(null);
    try {
      let res = await signUp.attemptEmailAddressVerification({ code });
      if (res.status === "complete") return done(res.createdSessionId);

      // 🔴 A NON-`complete` STATUS AFTER A GOOD CODE IS NOT A VERIFICATION FAILURE, AND SAYING SO
      // SENDS THE USER TO RETYPE A CODE THAT WORKED. `missing_requirements` means the code was
      // accepted and something else is outstanding. The recoverable case is legal consent, which
      // `signUp.update` can still supply — so try that once before reporting anything.
      if (res.status === "missing_requirements" && (res.missingFields || []).includes("legal_accepted") && agree) {
        try { res = await signUp.update({ legalAccepted: true }); } catch { /* falls through to the honest report */ }
        if (res.status === "complete") return done(res.createdSessionId);
      }

      // Anything still outstanding is NAMED. A generic "verification couldn't be completed" is
      // exactly what hid this defect for the length of the production instance's life.
      const missing = (res.missingFields || []).join(", ");
      fail({ __cc: missing
        ? `Your email is verified, but the account still needs: ${missing}. Please contact support@citeclerk.com — this is on our side, not yours.`
        : `Your email is verified, but the account couldn’t be completed (${res.status}). Please contact support@citeclerk.com — this is on our side, not yours.` });
    } catch (e) { fail(e); }
  };

  const resend = async () => {
    setError(null);
    try { await signUp.prepareEmailAddressVerification({ strategy: "email_code" }); }
    catch (e) { setError(fmtErr(e)); }
  };

  const google = async () => {
    if (!isLoaded) return;
    setError(null);
    try {
      await signUp.authenticateWithRedirect({
        strategy: "oauth_google",
        redirectUrl: window.location.origin + "/citations",
        redirectUrlComplete: afterUrl || "/citations",
      });
    } catch (e) { fail(e); }
  };

  const onKey = (fn) => (e) => { if (e.key === "Enter") { e.preventDefault(); fn(); } };

  if (step === "done") {
    return (
      <div style={{ textAlign: "center", padding: "20px 0 10px" }}>
        <div style={{ width: 52, height: 52, borderRadius: "50%", background: "rgba(0,58,143,0.10)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 18px" }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--cc-accent-text, #003A8F)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
        </div>
        <div style={{ fontFamily: "'DM Serif Display', Georgia, serif", fontSize: 22, color: "var(--cc-ink, #1a2a4a)", marginBottom: 10 }}>Account created</div>
        <div className="cc-auth-meta">Taking you to the app…</div>
      </div>
    );
  }

  if (step === "verify") {
    return (
      <>
        <div className="cc-auth-meta">We emailed a 6-digit code to <strong>{email}</strong></div>
        <div className="cc-auth-field">
          <label className="cc-auth-label" htmlFor="cc-su-code">Verification code</label>
          <input id="cc-su-code" className="cc-auth-input" type="text" inputMode="numeric"
            value={code} autoFocus placeholder="Enter the 6-digit code"
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            onKeyDown={onKey(submitCode)} />
        </div>
        {error && <div className="cc-auth-error">{error.msg || error}{error.offerSignIn && (<>{" "}<button type="button" className="cc-auth-error-link" onClick={onSwitchToSignIn}>Sign in instead →</button></>)}</div>}
        <button className="cc-auth-primary" disabled={loading || code.length < 6} onClick={submitCode}>
          {loading ? <><Spinner/>Verifying…</> : "Verify email"}
        </button>
        <button className="cc-auth-back" onClick={resend} disabled={loading}>Resend code</button>
      </>
    );
  }

  // step === "form"
  return (
    <>
      <div style={{ display: "flex", gap: 12 }}>
        <div className="cc-auth-field" style={{ flex: 1 }}>
          <label className="cc-auth-label" htmlFor="cc-su-first">First name</label>
          <input id="cc-su-first" className="cc-auth-input" type="text" value={first} autoFocus
            placeholder="First" onChange={(e) => setFirst(e.target.value)} onKeyDown={onKey(submitForm)} />
        </div>
        <div className="cc-auth-field" style={{ flex: 1 }}>
          <label className="cc-auth-label" htmlFor="cc-su-last">Last name</label>
          <input id="cc-su-last" className="cc-auth-input" type="text" value={last}
            placeholder="Last" onChange={(e) => setLast(e.target.value)} onKeyDown={onKey(submitForm)} />
        </div>
      </div>
      <div className="cc-auth-field">
        <label className="cc-auth-label" htmlFor="cc-su-email">Email address</label>
        <input id="cc-su-email" className="cc-auth-input" type="email" value={email}
          placeholder="you@firm.com" onChange={(e) => setEmail(e.target.value)} onKeyDown={onKey(submitForm)} />
      </div>
      <div className="cc-auth-field">
        <label className="cc-auth-label" htmlFor="cc-su-pw">Password</label>
        <div className="cc-auth-pw-wrap">
          <input id="cc-su-pw" className="cc-auth-input" type={showPw ? "text" : "password"}
            value={password} placeholder="Create a password"
            onChange={(e) => setPassword(e.target.value)} onKeyDown={onKey(submitForm)} />
          <button type="button" className="cc-auth-pw-toggle"
            aria-label={showPw ? "Hide password" : "Show password"}
            onClick={() => setShowPw((s) => !s)}>
            {showPw ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z"/><circle cx="12" cy="12" r="3"/></svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
            )}
          </button>
        </div>
      </div>
      <div className="cc-auth-field">
        <label className="cc-auth-label" htmlFor="cc-su-ref">
          Referral code <span style={{ color: "var(--cc-ink-muted, #8ca0c4)", fontWeight: 400 }}>(optional)</span>
        </label>
        <input id="cc-su-ref" className="cc-auth-input" type="text" value={referral}
          placeholder="Enter a code if you have one"
          onChange={(e) => { setReferral(e.target.value.toUpperCase()); setReferralErr(""); setReferralOk(false); }}
          onBlur={() => checkReferral(referral)}
          onKeyDown={onKey(submitForm)} />
        {referralErr && (
          <div style={{ marginTop: 5, fontFamily: "'DM Sans', system-ui, sans-serif", fontSize: 12, color: "var(--cc-error-text, #c62828)" }}>
            {referralErr}
          </div>
        )}
        {!referralErr && referralOk && (
          <div style={{ marginTop: 5, fontFamily: "'DM Sans', system-ui, sans-serif", fontSize: 12, color: "var(--cc-success-text, #1b7a3d)" }}>
            Code applied — you'll both get 20 citations once you verify your .edu and run your first citation.
          </div>
        )}
      </div>
      {/* 🔴 className="cc-auth-agree" ADDED BY RUN GP, AND IT IS THE ONLY THING THAT MAKES RUN FU'S
          TAP FIX REACH THIS ROW. FU added a 44px min-height rule for .cc-auth-agree, the consent
          control; AccountChrome's copy of this label carries that class, THIS one carried no class at
          all, and this is the copy that renders on the marketing pages. Measured before: a 16x16
          checkbox inside a 39px label. The rule already existed — it just had nothing to match. */}
      <label className="cc-auth-agree" style={{ display: "flex", alignItems: "flex-start", gap: 8, fontFamily: "'DM Sans', system-ui, sans-serif", fontSize: 13, color: "var(--cc-ink-secondary, #6B86B5)", lineHeight: 1.5, cursor: "pointer" }}>
        <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)}
          style={{ marginTop: 2, width: 16, height: 16, accentColor: "var(--cc-accent-fill, #003A8F)", flexShrink: 0, cursor: "pointer" }} />
        <span>I agree to the{" "}
          <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: "var(--cc-accent-text, #003A8F)", fontWeight: 600 }}>Terms of Service</a>{" "}and{" "}
          <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: "var(--cc-accent-text, #003A8F)", fontWeight: 600 }}>Privacy Policy</a>.
        </span>
      </label>
      {error && <div className="cc-auth-error">{error.msg || error}{error.offerSignIn && (<>{" "}<button type="button" className="cc-auth-error-link" onClick={onSwitchToSignIn}>Sign in instead →</button></>)}</div>}
      <button className="cc-auth-primary"
        disabled={loading || !first || !last || !email || !password || !agree}
        onClick={submitForm}>
        {loading ? <><Spinner/>Creating account…</> : "Create account"}
      </button>
      {/* Clerk Smart CAPTCHA mounts here when bot protection is on; renders nothing otherwise. */}
      <div id="clerk-captcha" />
      <div className="cc-auth-divider">or</div>
      <button className="cc-auth-google" onClick={google} disabled={loading}>
        <GoogleIcon /> Continue with Google
      </button>
      <div className="cc-auth-switch">
        Already have an account?{" "}
        <button onClick={onSwitchToSignIn}>Sign in</button>
      </div>
    </>
  );
}

// ── Modal shell + Clerk interception ──
// App-controlled opener — the ROBUST path for the React SPA (the window.Clerk.openSignIn
// global-patch below is unreliable under Clerk-React's management, so /app fell through to
// Clerk's stock modal). AuthModal registers its setState here on mount (no window.Clerk
// dependency); SPA triggers call openAuthModal() directly → the custom island opens
// deterministically, never Clerk's stock UI. (Marketing's vanilla pages still use the
// window.Clerk override via the self-mount IIFE.)
let _openAuthModal = null;
export function openAuthModal(mode = "signin") {
  if (_openAuthModal) _openAuthModal(mode === "signup" ? "signup" : "signin");
}

export function AuthModal() {
  const clerk = useClerk();
  const [state, setState] = useState({ open: false, mode: "signin", afterUrl: null });

  // Register the app-controlled opener (runs on mount; independent of window.Clerk timing).
  useEffect(() => {
    _openAuthModal = (mode) => setState({ open: true, mode, afterUrl: null });
    // B-HOME-4: page-level open bridge — static markup dispatches a CustomEvent; no
    // Clerk API is involved anywhere in the calling path.
    const onOpenEvt = (e) => setState({ open: true, mode: e?.detail?.mode === "signup" ? "signup" : "signin", afterUrl: e?.detail?.afterUrl || null });
    window.addEventListener("cc:open-auth", onOpenEvt);
    // RUN O — /referral/<CODE> serves the homepage with __CC_REFERRAL_CODE__ stamped into <head>.
    // Open sign-up on arrival. Also opens for a code the server REFUSED, because the whole point is
    // that a bad link says so instead of quietly presenting an ordinary blank homepage.
    if (readReferralFromPage().code || window.__CC_REFERRAL_INVALID__) {
      setState({ open: true, mode: "signup", afterUrl: null });
    }
    return () => { if (_openAuthModal) _openAuthModal = null; };
  }, []);

  useEffect(() => {
    injectStyles();
    if (!window.Clerk) return;
    const origSignIn = window.Clerk.openSignIn ? window.Clerk.openSignIn.bind(window.Clerk) : null;
    const origSignUp = window.Clerk.openSignUp ? window.Clerk.openSignUp.bind(window.Clerk) : null;

    // Intercept both → our Ultramarine modal, opened to the right mode.
    window.Clerk.openSignIn = (opts) =>
      setState({ open: true, mode: "signin", afterUrl: opts?.afterSignInUrl || opts?.redirectUrl || null });
    window.Clerk.openSignUp = (opts) =>
      setState({ open: true, mode: "signup", afterUrl: opts?.afterSignUpUrl || opts?.redirectUrl || null });
    window.__cc_nativeOpenSignUp = origSignUp; // kept as a safety reference

    return () => {
      if (window.Clerk && origSignIn) window.Clerk.openSignIn = origSignIn;
      if (window.Clerk && origSignUp) window.Clerk.openSignUp = origSignUp;
      window.removeEventListener("cc:open-auth", onOpenEvt);
    };
  }, [clerk]);

  /* ══ RUN AN — signup_started ═══════════════════════════════════════════════════════════════
     🔴 INSTRUMENTED AS A STATE TRANSITION, NOT AT THE CALL SITES, BECAUSE THERE ARE FIVE CALL
     SITES AND THEY ARE NOT A CLOSED SET. A signup modal opens from: openAuthModal("signup") (the
     SPA path), the `cc:open-auth` CustomEvent (static marketing markup), a /referral/<CODE>
     arrival, the window.Clerk.openSignUp patch (the Clark widget and the header island both go
     through it), and switchToSignUp from inside the sign-in tab. Instrumenting each one means the
     sixth route ships uncounted and nobody notices, because an event that is merely LOW looks
     exactly like an event that is CORRECT. Every one of those five ends here, in `state`.

     ⚠️ FIRES ONCE PER OPEN EPISODE — this is the part that needed care, since an effect on a
     changing dependency is otherwise the classic way to inflate a funnel number:
       • opened straight to signup            → 1
       • opened to signin, user switches      → 1 (at the switch, which IS the signup start)
       • user toggles signup→signin→signup    → still 1; `counted` is not cleared by a mode change
       • closed and reopened                  → 1 more, correctly: that is a new attempt
     `counted` is cleared ONLY on close, which is what makes the toggle case idempotent. A plain
     `[state.mode]` effect double-counts every toggle, and under the SPA's StrictMode it would
     double-count every open as well. */
  const signupCounted = useRef(false);
  useEffect(() => {
    if (!state.open) { signupCounted.current = false; return; }
    if (state.mode !== "signup" || signupCounted.current) return;
    signupCounted.current = true;
    track("signup_started");
  }, [state.open, state.mode]);

  const close = useCallback(() => setState((s) => ({ ...s, open: false })), []);

  useEffect(() => {
    document.body.style.overflow = state.open ? "hidden" : "";
    const onKey = (e) => { if (e.key === "Escape" && state.open) close(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [state.open, close]);

  const switchToSignUp = useCallback(() => setState((s) => ({ ...s, open: true, mode: "signup" })), []);
  const switchToSignIn = useCallback(() => setState((s) => ({ ...s, open: true, mode: "signin" })), []);

  return (
    <div className={"cc-auth-root" + (state.open ? " is-open" : "")}
      role="dialog" aria-modal="true" aria-labelledby="cc-auth-title">
      <div className="cc-auth-scrim" aria-hidden="true" onClick={close} />
      <div className="cc-auth-panel">
        <div className="cc-auth-header">
          <button type="button" className="cc-auth-close" aria-label="Close" onClick={close}>✕</button>
          <CiteClerkLogo />
          <h2 className="cc-auth-title" id="cc-auth-title">
            {state.mode === "signup" ? "Create your account" : "Sign in"}
          </h2>
          {state.mode !== "signup" && (
            <p className="cc-auth-subtitle">Welcome back</p>
          )}
        </div>
        <div className="cc-auth-body">
          {state.open && state.mode === "signup" && (
            <SignUpFlow afterUrl={state.afterUrl} close={close} onSwitchToSignIn={switchToSignIn} />
          )}
          {state.open && state.mode !== "signup" && (
            <SignInFlow afterUrl={state.afterUrl} close={close} onSwitchToSignUp={switchToSignUp} />
          )}
        </div>
      </div>
    </div>
  );
}

const mount = document.getElementById("cc-auth-react-root");
if (mount && PUBLISHABLE_KEY && PUBLISHABLE_KEY.indexOf("PLACEHOLDER") === -1) {
  waitForClerk(8000).then((clerk) => {
    if (!clerk) return;
    createRoot(mount).render(
      <ClerkProvider Clerk={clerk} publishableKey={PUBLISHABLE_KEY}>
        <AuthModal />
      </ClerkProvider>
    );
  });
}
